import React from 'react';
import { Handle, Position } from 'reactflow';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { FlowAction } from '~/graphql/types';
import { ClientFlowActionLfaCreateRequest } from '~/graphql/types.client';
import getCardHeading from '../../../../utils/getCardHeading';
import CardTemplate, { handleStyle, Props as CardProps } from '../CardTemplate';
import Divider from '~/components/atom/Divider';
import IssuesForAction from '../IssuesForAction';
import TemplateString from '../TemplateString';
import ActionLabelAsText from '../../../ActionLabelAsText';
import { getPathRepresentation } from '../../../../../UpdateAction/components/ConditionEditorV2/utils/getLabelForRep';
import useRelativeMaps from '../../../../hooks/useRelativeMaps';
import getPathForPointer from '../../../../../UpdateAction/components/Selector/utils/getPathForPointer';
import isPointerArgument from '~/components/page/Automation/v2/util/isPointerArgument';
import InputLabel from '~/components/atom/InputLabel';
import { Body } from '~/components/atom/Typography';

export type Props = Omit<
  CardProps<ClientFlowActionLfaCreateRequest>,
  'heading' | 'actionType'
> & {
  dataTestId?: string;
};

const LfaCreateRequestCard: React.FCC<Props> = React.memo(
  ({ selected, disabled, data, ...props }) => {
    const maps = useRelativeMaps({ actionId: data.action.id });
    const action = data.action;

    let variableName: string | null = null;
    if (action.event && isPointerArgument(action.event)) {
      const path = getPathForPointer(action.event.pointer, {
        subjectMap: maps.subjectMap,
        instanceMap: maps.instanceMap,
        directoryMap: maps.directoryMap,
      });

      if (path.error === undefined) {
        variableName = getPathRepresentation(path.result);
      }
    }

    return (
      <>
        <Handle
          type="target"
          position={Position.Top}
          style={handleStyle}
          isConnectable={false}
        />
        <CardTemplate
          {...props}
          actionType={FlowAction.LfaCreateRequest}
          heading={getCardHeading(FlowAction.LfaCreateRequest)}
          selected={selected}
          disabled={disabled}
          data={data}
        >
          <JustificationContainer direction="column" width="100%">
            <InputLabel label="Notitie" />
            {data.action.note.template &&
            data.action.note.template.length > 0 ? (
              <TemplateString
                templateString={data.action.note}
                actionId={data.action.id}
              />
            ) : (
              <Body margin={[null]}>-</Body>
            )}
            <Divider margin={['base', null]} />
            <InputLabel label="Triggering event" margin={[null, null, 's']} />
            <ActionLabelAsText str={variableName} highlightLabel />
          </JustificationContainer>
          <IssuesForAction
            margin={['base', null, null]}
            actionId={data.action.id}
          />
        </CardTemplate>
        <Handle
          type="source"
          position={Position.Bottom}
          style={handleStyle}
          isConnectable={false}
        />
      </>
    );
  },
);

export default LfaCreateRequestCard;
